import { useCallback, useState } from 'react';
import { startSprint } from '../../api/endpoints';
import { toApiError } from '../../api/errors';
import type { Id, Sprint } from '../../api/types';

/**
 * STARTING A SPRINT — `planned` → `active`. The half of PH-23 Or actually went looking for.
 *
 * ⭐ NO CONFIRMATION DIALOG, and that is a decision rather than an omission. `useCompleteSprint` asks
 * first because closing is IRREVERSIBLE and MOVES EVERY UNFINISHED TICKET somewhere else. Starting
 * does neither: it changes one column, moves nothing, and the API offers no `/unstart` only because
 * nobody has needed one — a sprint started by mistake is closed, which is the same button the team
 * would press a fortnight later anyway. ⇒ One tap, which is what a phone deserves for a
 * non-destructive action.
 *
 * 🔴🔴 THE 409 IS THE ENTIRE POINT OF THIS HOOK, AND IT IS SURFACED VERBATIM.
 *   db/001 permits ONE active sprint per project. `startSprint` pre-checks and answers:
 *
 *       this project already has an active sprint ('APFS v1.0.33'); close it before starting another
 *
 *   ⛔ "Could not start sprint" IS THE FAILURE MODE HERE, not the fallback. The blocking sprint may
 *   be off screen, in a collapsed section, or started by somebody else five minutes ago — the NAME in
 *   that sentence is the only thing on the screen that tells the reader what to go and do. The API
 *   grew a pre-check purely so this message would exist; throwing it away in the client would undo
 *   that work and leave the user pressing a button that can only ever fail.
 *
 * ⚠️ The other 409s (`this sprint is already active`, `a 'closed' sprint cannot be started; only a
 *   planned sprint can`) travel the same path — both mean somebody else changed the sprint since this
 *   page loaded, which "Could not start" also fails to say.
 *
 * ⭐ THE REFUSAL GOES TO A PAGE-LEVEL NOTICE, because there is no dialog to hold it. That notice sits
 *   ABOVE the scrolling section list in `SprintBacklogPage`, so it is visible at 390px however far
 *   down the page the button was — the same place a refused reorder reports itself.
 */
export interface StartSprintState {
  /** The sprint whose start is in flight, so only ITS button says "Starting…". */
  busyId: Id | null;
  /** The API's own words. */
  error: string | null;
  outcome: string | null;
  start: (sprint: Sprint) => void;
  dismissError: () => void;
  dismissOutcome: () => void;
}

export function useStartSprint(onStarted: (sprint: Sprint) => void): StartSprintState {
  const [busyId, setBusyId] = useState<Id | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const start = useCallback(
    (sprint: Sprint) => {
      /**
       * ⚠️ RE-ENTRY GUARD, AND IT IS NOT THE LINE UNDER TEST — MEASURED, NOT ASSUMED.
       *   Deleting this line leaves the suite GREEN, because the button carries `disabled={starting}`
       *   and jsdom (like a browser) does not run a click handler on a disabled control, so the path
       *   is unreachable through the UI. Deleting the `disabled` attribute instead turns the suite
       *   RED. ⇒ The attribute is the protection that works; this is the second line behind it, and
       *   saying so is more useful than a comment implying both are proven.
       *   ⭐ Same finding, same shape, as the note in `useCompleteSprint.cancel`.
       */
      if (busyId !== null) return;

      setBusyId(sprint.id);
      setError(null);
      setOutcome(null);

      void (async () => {
        try {
          const started = await startSprint(sprint.id);
          // ⭐ The server's own row, so the name shown is the one that was actually started — not the
          //   one this page happened to be holding.
          setOutcome(`${started.name} is now the active sprint.`);
          onStarted(started);
        } catch (caught) {
          const apiError = toApiError(caught, `/sprints/${sprint.id}/start`);
          // ⛔ `reason` FIRST. `apiError.message` for a 409 is the generic "That change was refused",
          //    which names no sprint and tells the reader nothing to do.
          setError(apiError.reason ?? apiError.message);
        } finally {
          setBusyId(null);
        }
      })();
    },
    [busyId, onStarted],
  );

  return {
    busyId,
    error,
    outcome,
    start,
    dismissError: () => setError(null),
    dismissOutcome: () => setOutcome(null),
  };
}
